import axios from 'axios';

export interface TranscriptionResponse {
  text: string;
  language: string;
  confidence?: number;
  duration?: number;
  provider: string;
  words?: any[];
}

export interface STTConfigResponse {
  enabled: boolean;
  provider: string;
  whisper_model?: string;
  ollama_model?: string;
  transformers_model?: string;
  available_providers: string[];
}

export interface ProviderInfo {
  name: string;
  display_name: string;
  available: boolean;
  configured: boolean;
  requires_api_key: boolean;
  supports_streaming: boolean;
  languages: string;
  cost: string;
  note?: string;
}

export interface ProvidersResponse {
  current_provider: string;
  enabled: boolean;
  providers: ProviderInfo[];
}

class SpeechToTextAPI {
  private baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

  /**
   * Transcribe audio blob to text
   * @param audioBlob Audio file as Blob
   * @param language Language code (e.g., "en", "es", "fr", "auto")
   * @param saveAudio Whether to save audio file on server (debugging)
   * @returns Transcription result
   */
  async transcribeAudio(
    audioBlob: Blob,
    language: string = 'en',
    saveAudio: boolean = false
  ): Promise<TranscriptionResponse> {
    const formData = new FormData();
    formData.append('audio_file', audioBlob, 'audio.webm');
    formData.append('language', language);
    formData.append('save_audio', saveAudio.toString());

    try {
      const response = await axios.post<TranscriptionResponse>(
        `${this.baseURL}/speech-to-text/transcribe`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000, // 120 second timeout for transcription
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('STT API error:', error);

      // Handle different error types
      if (error.response) {
        const errorDetail = error.response.data?.detail;

        // Check if error has fallback information
        if (errorDetail && typeof errorDetail === 'object') {
          const errorMsg = errorDetail.error || 'Transcription failed';
          const fallback = errorDetail.fallback || 'Please try voice message recording instead';
          const suggestion = errorDetail.suggestion || '';

          throw new Error(`${errorMsg}\n\n${fallback}${suggestion ? `\n\n${suggestion}` : ''}`);
        } else {
          throw new Error(errorDetail || 'Transcription failed');
        }
      } else if (error.request) {
        // Network error
        throw new Error(
          'Unable to reach speech-to-text service. Please check your connection or try voice message recording instead.'
        );
      } else {
        throw new Error('An unexpected error occurred. Please try voice message recording instead.');
      }
    }
  }

  /**
   * Get current STT configuration
   * @returns STT configuration
   */
  async getConfig(): Promise<STTConfigResponse> {
    try {
      const response = await axios.get<STTConfigResponse>(
        `${this.baseURL}/speech-to-text/config`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching STT config:', error);
      throw new Error('Failed to fetch STT configuration');
    }
  }

  /**
   * Get list of available STT providers
   * @returns Provider information
   */
  async getProviders(): Promise<ProvidersResponse> {
    try {
      const response = await axios.get<ProvidersResponse>(
        `${this.baseURL}/speech-to-text/providers`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching STT providers:', error);
      throw new Error('Failed to fetch STT providers');
    }
  }

  /**
   * Check if STT service is available
   * @returns true if available, false otherwise
   */
  async isAvailable(): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.enabled && config.available_providers.length > 0;
    } catch (error) {
      return false;
    }
  }
}

export const speechToTextAPI = new SpeechToTextAPI();
export default speechToTextAPI;
